'use client';

import { api } from '@/lib/api';
import { formatTestDate } from '@/shared/utils/date';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { JobTest, JobTestWithStats } from '../types';
import { useDetailedStats } from './useDetailedStats';

export interface CreateTestResponse {
  result: string;
  id: string;
}

/**
 * Fetch all tests for a job
 * @param jobId ID of the job to fetch tests for
 */
const fetchJobTests = async (jobId: string): Promise<JobTest[]> => {
  const response = await api.get<JobTest[]>(`/v2/p2p/${jobId}/tests`);
  return response.data;
};

/**
 * Create a new test for a job
 * @param jobId ID of the job to create a test for
 */
const createJobTest = async (jobId: string): Promise<CreateTestResponse> => {
  const response = await api.post<CreateTestResponse>(`/v2/p2p/${jobId}/tests`);
  return response.data;
};

/**
 * Hook for fetching a single job test with its detailed stats
 * @param jobId ID of the parent job
 * @param testId ID of the test to fetch, or null to fetch the first test
 */
export const useJobTest = (jobId: string, testId: string | null) => {
  const {
    data: tests,
    isLoading: isTestsListLoading,
    error: testsListError,
    refetch: refetchTestsList,
  } = useJobTests(jobId);

  let derivedTest: JobTest | null = null;
  let derivedTestError: Error | null = null;

  if (!jobId) {
    derivedTestError = new Error('Job ID missing');
  } else if (!isTestsListLoading && !testsListError) {
    if (tests) {
      if (!testId) {
        derivedTest = tests.length > 0 ? tests[0] : null;
      } else {
        const foundTest = tests.find((t) => t.p2p_id === testId);
        derivedTest = foundTest || null;
        if (!foundTest) {
          // Only set an error if a specific testId was given and not found.
          // If tests array is empty, this is a valid "not found" state.
          // If tests array is not empty but testId is not in it, it's a specific "not found".
          derivedTestError = new Error(`Test with ID ${testId} not found.`);
        }
      }
    } else {
      // tests is undefined, but no loading and no list error - means empty result from query
      if (testId) {
        derivedTestError = new Error(
          `Test with ID ${testId} not found; no tests available for this job.`
        );
      }
      // If no testId, derivedTest remains null, which is fine for "no tests".
    }
  }

  const shouldEnableStats =
    !!derivedTest?.p2p_id &&
    !!derivedTest?.account_id &&
    !!derivedTest?.start_date &&
    !!derivedTest?.end_date;

  const statsQuery = useDetailedStats({
    jobId: shouldEnableStats ? derivedTest!.p2p_id : '',
    accountId: shouldEnableStats ? derivedTest!.account_id : '',
    startDate: shouldEnableStats ? derivedTest!.start_date : '',
    endDate: shouldEnableStats ? derivedTest!.end_date : '',
  });

  const overallIsLoading =
    isTestsListLoading || (statsQuery.isLoading && shouldEnableStats);

  let overallError: Error | null = null;
  if (testsListError) {
    overallError = testsListError;
  } else if (derivedTestError) {
    overallError = derivedTestError;
  } else if (shouldEnableStats && statsQuery.error) {
    overallError = statsQuery.error;
  }

  const p2pIdForCallback = derivedTest?.p2p_id;
  const accountIdForCallback = derivedTest?.account_id;
  const startDateForCallback = derivedTest?.start_date;
  const endDateForCallback = derivedTest?.end_date;

  const refetch = useCallback(async () => {
    await refetchTestsList();
    if (
      p2pIdForCallback &&
      accountIdForCallback &&
      startDateForCallback &&
      endDateForCallback
    ) {
      // statsQuery.refetch is stable and uses the parameters it was initialized with.
      // If these parameters (derived from derivedTest) changed due to refetchTestsList,
      // useDetailedStats would re-fetch. This explicit call is for cases where parameters
      // didn't change but an underlying data refresh is needed.
      await statsQuery.refetch();
    }
  }, [
    refetchTestsList,
    p2pIdForCallback,
    accountIdForCallback,
    startDateForCallback,
    endDateForCallback,
    statsQuery,
  ]);

  const result: JobTestWithStats = {
    test: derivedTest,
    stats: shouldEnableStats && statsQuery.data ? statsQuery.data : null,
    isLoading: overallIsLoading,
    error: overallError,
    refetch: refetch,
  };

  return result;
};

/**
 * Hook for fetching all tests for a job
 * @param jobId ID of the job to fetch tests for
 */
export const useJobTests = (jobId: string) => {
  return useQuery<JobTest[], Error>({
    queryKey: ['jobTests', jobId],
    queryFn: () => {
      return fetchJobTests(jobId);
    },
    enabled: !!jobId,
  });
};

/**
 * Hook for creating a new test for a job
 * @param jobId ID of the job to create a test for
 */
export const useCreateJobTest = (jobId: string) => {
  return useMutation<CreateTestResponse, Error, void>({
    mutationKey: ['createJobTest', jobId],
    mutationFn: async () => {
      const responseData = await createJobTest(jobId);

      if (
        !responseData ||
        typeof responseData.id !== 'string' ||
        typeof responseData.result !== 'string'
      ) {
        console.error(
          '[useCreateJobTest] Invalid data structure received from service:',
          responseData
        );
        throw new Error('Invalid data structure received from createJobTest service');
      }

      return responseData;
    },
  });
};

/**
 * Format a test creation date for display
 * @param dateString ISO date string in UTC
 * @returns Formatted date string in local timezone
 */
export const formatTestCreationDate = (dateString: string): string => {
  return formatTestDate(dateString);
};

/**
 * Interface for the send test message parameters
 */
interface SendTestMessageParams {
  testJobId: string;
  testContactPhone: string;
  testListId: string;
}

/**
 * Send a test message to a contact
 */
const sendTestMessage = async ({
  testJobId,
  testContactPhone,
  testListId,
}: SendTestMessageParams) => {
  const response = await api.post(`/1to1/jobs/${testJobId}/send_test_message`, {
    test_contact_phone: testContactPhone,
    test_list_id: testListId,
  });
  return response.data;
};

/**
 * Hook for sending a test message to a contact
 */
export const useSendTestMessage = () => {
  return useMutation({
    mutationFn: sendTestMessage,
  });
};
